import { getFinalEvrScore } from "../../../utils/evrscore.utils";
import {
  isSingleSleeperSelector,
  primarySleeperFirmnessSelector,
  primarySleeperFirstNameSelector,
  primarySleeperHeightSelector,
  primarySleeperLastSelectedPainPointSelector,
  primarySleeperPainPointsSelector,
  primarySleeperPrimarySleepStyleSelector,
  primarySleeperUnitSystemSelector,
  primarySleeperWeightSelector,
  secondarySleeperFirmnessSelector,
  secondarySleeperFirstNameSelector,
  secondarySleeperHeightSelector,
  secondarySleeperLastSelectedPainPointSelector,
  secondarySleeperPainPointsSelector,
  secondarySleeperPrimarySleepStyleSelector,
  secondarySleeperSecondarySleepStyleSelector,
  secondarySleeperUnitSystemSelector,
  secondarySleeperWeightSelector,
} from "../../../redux/slices/mattressConfiguration";
import { useSelector } from "react-redux";
import { useTheme } from "@mui/styles";
import { useTranslation } from "react-i18next";
import React from "react";
import styled from "styled-components";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  getBedSizeName,
  getPainPointsName,
  getSleepStyleName,
  hasPainPoints,
  painPointsIndexReference,
} from "../../../utils/bedBuilder.utils";
import { getAdaptiveFontSize } from "../../../utils/styling.utils";
import { currentlyEditingSleeperSelector } from "../../../redux/slices/customNavigation";

const StyledResultTableContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-direction: column;
  width: 100%;
  height: auto;
`;

const StyledRowContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-direction: row;
  width: 100%;
  border-width: 0 0 1px 0;
  border-style: solid dotted inset double;
  border-color: #ade1fa;
`;

const StyledTitle = styled.div`
  font-weight: 600;
  font-family: noto-sans;
`;

const StyledValue = styled.div`
  font-weight: 300;
  font-family: noto-sans;
`;

const SingleSleeperRow = ({
  firstColumn,
  secondColumn,
  isForDoubleSleeperOnly,
}) => {
  const theme = useTheme();
  const isSmartphoneScreen = useMediaQuery("(min-width: 0px)");
  const isSmallScreen = useMediaQuery("(min-width: 500px)");
  const isMediumScreen = useMediaQuery("(min-width: 768px)");
  const isLargeScreen = useMediaQuery("(min-width: 992px)");
  const isExtraLargeScreen = useMediaQuery("(min-width: 1200px)");
  const screenWidthHooks = {
    isSmartphoneScreen,
    isSmallScreen,
    isMediumScreen,
    isLargeScreen,
    isExtraLargeScreen,
  };

  if (!isForDoubleSleeperOnly) {
    return (
      <StyledRowContainer
        className="row"
        style={{
          width: isMediumScreen ? "80%" : "100%",
          height: isMediumScreen ? "3vh" : "35px",
        }}
      >
        <StyledTitle
          className="col-7 text-start"
          style={{
            color: theme.COLORS.BLUE.darkBlueGreen,
            fontSize: isMediumScreen
              ? getAdaptiveFontSize({
                  screenWidthHooks,
                  fontSize: 0.9,
                })
              : "0.80rem",
          }}
        >
          {firstColumn}
        </StyledTitle>
        <StyledValue
          className="col-5 text-start"
          style={{
            color: theme.COLORS.BLUE.darkBlueGreen,
            fontSize: isMediumScreen
              ? getAdaptiveFontSize({
                  screenWidthHooks,
                  fontSize: 1,
                })
              : "0.8rem",
          }}
        >
          {secondColumn}
        </StyledValue>
      </StyledRowContainer>
    );
  } else {
    return null;
  }
};

const DoubleSleeperRow = ({
  firstColumn,
  secondColumn,
  thirdColumn,
  isForDoubleSleeperOnly,
}) => {
  const theme = useTheme();
  //Media Queries
  const isSmartphoneScreen = useMediaQuery("(min-width: 0px)");
  const isSmallScreen = useMediaQuery("(min-width: 500px)");
  const isMediumScreen = useMediaQuery("(min-width: 768px)");
  const isLargeScreen = useMediaQuery("(min-width: 992px)");
  const isExtraLargeScreen = useMediaQuery("(min-width: 1200px)");
  const screenWidthHooks = {
    isSmartphoneScreen,
    isSmallScreen,
    isMediumScreen,
    isLargeScreen,
    isExtraLargeScreen,
  };
  return (
    <StyledRowContainer
      className="row"
      style={{
        borderWidth: isForDoubleSleeperOnly ? "0" : "0 0 1px 0",
        height: isMediumScreen ? "3vh" : "35px",
      }}
    >
      <StyledTitle
        className="col-5 text-start"
        style={{
          color: theme.COLORS.BLUE.darkBlueGreen,
          fontSize: isMediumScreen
            ? isForDoubleSleeperOnly
              ? "1rem"
              : "0.75rem"
            : "0.75rem",
        }}
      >
        {firstColumn}
      </StyledTitle>
      <StyledValue
        className="col-4 text-start"
        style={{
          color: theme.COLORS.BLUE.darkBlueGreen,
          fontWeight: isForDoubleSleeperOnly ? "bold" : "normal",
          fontSize: isMediumScreen
            ? isForDoubleSleeperOnly
              ? "1rem"
              : "0.87rem"
            : "0.75rem",
        }}
      >
        {secondColumn}
      </StyledValue>
      <StyledValue
        className="col-3 text-start"
        style={{
          color: theme.COLORS.BLUE.darkBlueGreen,
          fontWeight: isForDoubleSleeperOnly ? "bold" : "normal",
          fontSize: isMediumScreen
            ? isForDoubleSleeperOnly
              ? "1rem"
              : "0.87rem"
            : "0.75rem",
        }}
      >
        {thirdColumn}
      </StyledValue>
    </StyledRowContainer>
  );
};

const ResultTable = () => {
  const { t } = useTranslation();
  const isSingleSleeper = useSelector(isSingleSleeperSelector);

  const currentlyEditingSleeper = useSelector(currentlyEditingSleeperSelector);

  const bedSize = useSelector(
    state => state.mattressConfiguration.mattressConfiguration.bedSize
  );

  const primarySleeperHeight = useSelector(primarySleeperHeightSelector);

  const primarySleeperUnitSystem = useSelector(
    primarySleeperUnitSystemSelector
  );
  const primarySleeperWeight = useSelector(primarySleeperWeightSelector);
  const primarySleeperPrimarySleepStyle = useSelector(
    primarySleeperPrimarySleepStyleSelector
  );
  const primarySleeperSecondarySleepStyle = useSelector(
    state =>
      state.mattressConfiguration.mattressBuilder.primarySleeper
        .secondarySleepStyle
  );
  const secondarySleeperUnitSystem = useSelector(
    secondarySleeperUnitSystemSelector
  );
  const primarySleeperPainPoints = useSelector(
    primarySleeperPainPointsSelector
  );
  const secondarySleeperPainPoints = useSelector(
    secondarySleeperPainPointsSelector
  );
  const primarySleeperLastSelectedPainPoint = useSelector(
    primarySleeperLastSelectedPainPointSelector
  );
  const secondarySleeperLastSelectedPainPoint = useSelector(
    secondarySleeperLastSelectedPainPointSelector
  );
  const secondarySleeperHeight = useSelector(secondarySleeperHeightSelector);

  const primarySleeperFirmness = useSelector(primarySleeperFirmnessSelector);
  const secondarySleeperFirmness = useSelector(
    secondarySleeperFirmnessSelector
  );
  const secondarySleeperWeight = useSelector(secondarySleeperWeightSelector);
  const secondarySleeperPrimarySleepStyle = useSelector(
    secondarySleeperPrimarySleepStyleSelector
  );
  const secondarySleeperSecondarySleepStyle = useSelector(
    secondarySleeperSecondarySleepStyleSelector
  );

  const primarySleeperFirstName = useSelector(primarySleeperFirstNameSelector);
  const secondarySleeperFirstName = useSelector(
    secondarySleeperFirstNameSelector
  );

  const getPrimarySleeperHeightString = () => {
    if (primarySleeperUnitSystem === "metric") {
      return `${primarySleeperHeight} cm`;
    } else {
      return primarySleeperHeight;
    }
  };
  const getSecondarySleeperHeightString = () => {
    if (secondarySleeperUnitSystem === "metric") {
      return `${secondarySleeperHeight} cm`;
    } else {
      return secondarySleeperHeight;
    }
  };

  const getPrimarySleeperWeightString = () => {
    if (primarySleeperUnitSystem === "metric") {
      return `${primarySleeperWeight} kg`;
    } else {
      return `${primarySleeperWeight} lbs`;
    }
  };
  const getSecondarySleeperWeightString = () => {
    if (secondarySleeperUnitSystem === "metric") {
      return `${secondarySleeperWeight} kg`;
    } else {
      return `${secondarySleeperWeight} lbs`;
    }
  };

  const getPrimarySleeperPainPointsString = () => {
    const painPointsIndex =
      painPointsIndexReference[primarySleeperLastSelectedPainPoint];
    const primarySleeperHasMultiplePainPoints =
      hasPainPoints(primarySleeperPainPoints).length > 1;
    return primarySleeperHasMultiplePainPoints
      ? t("Mult. Pain Points")
      : getPainPointsName(painPointsIndex);
  };

  const getSecondarySleeperPainPointsString = () => {
    const painPointsIndex =
      painPointsIndexReference[secondarySleeperLastSelectedPainPoint];
    const secondarySleeperHasMultiplePainPoints =
      hasPainPoints(secondarySleeperPainPoints).length > 1;
    return secondarySleeperHasMultiplePainPoints
      ? t("Mult. Pain Points")
      : getPainPointsName(painPointsIndex);
  };

  const getFirmnessString = (value) => {
    switch (value) {
    case 1:
      return '1 - Soft';
    case 2:
      return '2 - Medium Soft';
    case 3:
      return '3 - Medium';
    case 4:
      return '4 - Medium Firm';
    case 5:
      return '5 - Firm';
    }
  };

  const tableRows = [
    {
      firstColumn: t("Category"),
      secondColumn: primarySleeperFirstName,
      thirdColumn: secondarySleeperFirstName,
      isForDoubleSleeperOnly: true,
    },
    {
      firstColumn: t(" SIZE:"),
      secondColumn: getBedSizeName(bedSize),
      thirdColumn: getBedSizeName(bedSize),
    },
    {
      firstColumn: t("SLEEP HEIGHT:"),
      secondColumn: getPrimarySleeperHeightString(),
      thirdColumn: getSecondarySleeperHeightString(),
    },
    {
      firstColumn: t("BODY WEIGHT:"),
      secondColumn: getPrimarySleeperWeightString(),
      thirdColumn: getSecondarySleeperWeightString(),
    },
    {
      firstColumn: t("SLEEP STYLE:"),
      secondColumn: getSleepStyleName(primarySleeperPrimarySleepStyle),
      thirdColumn: getSleepStyleName(secondarySleeperPrimarySleepStyle),
    },
    {
      firstColumn: t("OTHER SLEEP STYLE:"),
      secondColumn: getSleepStyleName(primarySleeperSecondarySleepStyle),
      thirdColumn: getSleepStyleName(secondarySleeperSecondarySleepStyle),
    },
    {
      firstColumn: t("PAIN POINTS:"),
      secondColumn: getPrimarySleeperPainPointsString(),
      thirdColumn: getSecondarySleeperPainPointsString(),
    },
    {
      firstColumn: t(" FIRMNESS PREFERENCE:"),
      secondColumn: getFirmnessString(primarySleeperFirmness),
      thirdColumn: getFirmnessString(secondarySleeperFirmness),
    },
    {
      firstColumn: t(" EVR-INDEX:"),
      secondColumn: getFinalEvrScore({ isPrimarySleeper: true }),
      thirdColumn: getFinalEvrScore({ isPrimarySleeper: false }),
    },
  ];

  return (
    <StyledResultTableContainer>
      {tableRows.map(element => {
        return (
          <>
            {isSingleSleeper && (
              <SingleSleeperRow
                key={element.firstColumn}
                firstColumn={element.firstColumn}
                secondColumn={element.secondColumn}
                isForDoubleSleeperOnly={element.isForDoubleSleeperOnly}
              ></SingleSleeperRow>
            )}
            {!isSingleSleeper && (
              <DoubleSleeperRow
                key={element.firstColumn}
                firstColumn={element.firstColumn}
                secondColumn={element.secondColumn}
                thirdColumn={element.thirdColumn}
                isForDoubleSleeperOnly={element.isForDoubleSleeperOnly}
              ></DoubleSleeperRow>
            )}
          </>
        );
      })}
    </StyledResultTableContainer>
  );
};

export default ResultTable;
